import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getOrderById } from 'src/services/OrderService';
import WalToast from 'src/utils/WalToast';

import { RootState } from './store';

// ─── State Interface ──────────────────────────────────────────────────────────

interface IOrderDetailState {
    loading: boolean;
    error: any;
    orderDetailsData: any; // main order object (was "order")
    orderActivityData: any[];
    orderLegs: any[];
    currentLegData: any;
    // Active leg tab — single source of truth for the leg tab selection (mirrors the
    // reference wal-frontend-v2 orderLegActiveTab). tabValue is `isMaster-${order.id}`
    // for the master tab or String(leg.id) for a leg tab.
    orderLegActiveTab: { tabValue: string; isMaster: boolean };
    currentLocation: any;
    isOrderHasLeg: boolean;
    isMasterOrder: boolean;
    activeLeg: any;
    params: {
        isModalOpenType: string | null;
        data: any;
    };
    isOrderServiceLoading: boolean;
    legDocuments: {
        viewDocument: any;
        deleteDocumentId: number | null;
        isDeleting: boolean;
    };
}

// ─── Initial Values ───────────────────────────────────────────────────────────

export const initialParams = {
    isModalOpenType: null,
    data: null,
};

const initialState: IOrderDetailState = {
    loading: false,
    error: null,
    orderDetailsData: null,
    orderActivityData: [],
    orderLegs: [],
    currentLegData: null,
    orderLegActiveTab: { tabValue: '', isMaster: false },
    currentLocation: null,
    isOrderHasLeg: false,
    isMasterOrder: true,
    activeLeg: null,
    params: initialParams,
    isOrderServiceLoading: false,
    legDocuments: {
        viewDocument: null,
        deleteDocumentId: null,
        isDeleting: false,
    },
};

// ─── Leg Tab Builder ──────────────────────────────────────────────────────────

/**
 * Build the leg selector tab array. Tab identity contract (mirrors wal-frontend-v2
 * OrderDetailV2 Overview):
 *  - master tab value = `isMaster-${order.id}`. WAL's leg-id rule keeps Leg 1's legId
 *    EQUAL to the order's orderId, so keying the master tab by orderId collided with
 *    Leg 1 — both tabs highlighted together and both selected leg 1's data.
 *  - leg tab value = String(leg.id) (numeric DB id). legId is a display string only.
 *  - master tab exists only when the order has more than one leg.
 * Used by getOrderDetailsAsync.fulfilled AND the ShareOrder page — keep them identical.
 */
export const buildOrderLegTabs = (masterOrder: any, orderLegsRaw: any[]): { value: string; name: string }[] => {
    const hasMultipleLegs = orderLegsRaw.length > 1;

    // Single presented leg (a direct order, or a multi-leg order fully consolidated by
    // Show-as-Multi-Leg OFF): the customer knows the shipment by its MASTER id, and the
    // leg's own id (#WAL-…-2472 for order #WAL-…-2471) reads as a different order — so
    // present only the master tab. The master tab's order-level rollups (first-leg
    // shipper, last-leg consignee, first-pickup/last-delivery schedule) equal the single
    // leg's data, so nothing is lost.
    if (!hasMultipleLegs) {
        return [
            {
                value: `isMaster-${masterOrder?.id}`,
                name: `Master #${masterOrder?.orderId}`,
            },
        ];
    }

    return [
        {
            value: `isMaster-${masterOrder?.id}`,
            name: `Master #${masterOrder?.orderId}`,
        },
        ...orderLegsRaw.map((leg: any) => ({
            value: `${leg?.id}`,
            name: `#${leg?.legId}`,
        })),
    ];
};

// ─── Async Thunk ──────────────────────────────────────────────────────────────

export const getOrderDetailsAsync: any = createAsyncThunk('orders/getOrderDetails', async (arg: string | { id: string; context?: 'tracking'; silent?: boolean }, { rejectWithValue }) => {
    // A plain id keeps every existing caller working; the Track page passes { id, context: 'tracking' },
    // and the socket live-sync passes { id, silent: true } for a background refresh.
    const orderId = typeof arg === 'string' ? arg : arg.id;
    const context = typeof arg === 'string' ? undefined : arg.context;
    const silent = typeof arg === 'string' ? false : !!arg.silent;

    try {
        const orderDetailsResponse = await getOrderById(orderId, { context });

        return { orderDetailsData: orderDetailsResponse.data };
    } catch (error: any) {
        // A silent refresh was never asked for by the customer — a toast for it would
        // pop out of nowhere. The page keeps showing the last good data instead.
        if (!silent) {
            WalToast.error(error?.response?.status === 404 ? error?.response?.data?.message : 'Orders failed to load');
        }

        return rejectWithValue(error);
    }
});

// ─── Slice ────────────────────────────────────────────────────────────────────

export const OrdersSlice = createSlice({
    name: 'orders',
    initialState,
    reducers: {
        // Primary reset — clears all order detail state
        resetOrderDetailsState: () => initialState,

        // Loading
        setOrderLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },

        // Order detail data (merge update — same pattern as reference setOrderDetailsData)
        setOrderDetailsData: (state, action: PayloadAction<any>) => {
            state.orderDetailsData = { ...state.orderDetailsData, ...action.payload };
        },

        setCurrentLegData: (state, action: PayloadAction<any>) => {
            state.currentLegData = action.payload;
        },

        setOrderLegActiveTab: (state, action: PayloadAction<{ tabValue: string; isMaster: boolean }>) => {
            state.orderLegActiveTab = action.payload;
        },

        // Modal / UI state management (same as reference setParams)
        setParams: (state, action: PayloadAction<any>) => {
            state.params = { ...state.params, ...action.payload };
        },

        // Partial field update on order
        updateOrderField: (state, action: PayloadAction<{ field: string; value: any }>) => {
            if (state.orderDetailsData) {
                state.orderDetailsData = { ...state.orderDetailsData, [action.payload.field]: action.payload.value };
            }
        },

        // Partial field update on active leg
        updateCurrentLegField: (state, action: PayloadAction<{ field: string; value: any }>) => {
            if (state.currentLegData) {
                state.currentLegData = { ...state.currentLegData, [action.payload.field]: action.payload.value };
            }
        },

        // Remove a document from currentLegData.documents by id
        removeLegDocument: (state, action: PayloadAction<number>) => {
            if (state.currentLegData?.documents) {
                state.currentLegData = {
                    ...state.currentLegData,
                    documents: state.currentLegData.documents.filter((doc: any) => doc.id !== action.payload),
                };
            }

            // Keep the master copy in sync — without this, switching to another leg tab and
            // back re-reads orderDetailsData.orderLegs[n].documents and resurrects the deleted doc.
            if (state.orderDetailsData?.orderLegs) {
                state.orderDetailsData = {
                    ...state.orderDetailsData,
                    orderLegs: state.orderDetailsData.orderLegs.map((leg: any) =>
                        Array.isArray(leg?.documents) ? { ...leg, documents: leg.documents.filter((doc: any) => doc.id !== action.payload) } : leg
                    ),
                };
            }
        },

        // Leg document modal/UI state — partial merge update
        setLegDocumentsState: (state, action: PayloadAction<Partial<IOrderDetailState['legDocuments']>>) => {
            state.legDocuments = { ...state.legDocuments, ...action.payload };
        },

        // Bulk set (backward-compat for components still using the old pattern)
        setOrderData: (
            state,
            action: PayloadAction<{
                order: any;
                orderLegs: any[];
                currentLegData: any;
                currentLocation: any;
                isOrderHasLeg: boolean;
            }>
        ) => {
            state.orderDetailsData = action.payload.order;
            state.orderLegs = action.payload.orderLegs;
            state.currentLegData = action.payload.currentLegData;
            state.currentLocation = action.payload.currentLocation;
            state.isOrderHasLeg = action.payload.isOrderHasLeg;
            // Default the active tab to the first tab in the provided array (master when
            // multi-leg, the single leg otherwise) — same contract as getOrderDetailsAsync.
            const firstTabValue = `${action.payload.orderLegs?.[0]?.value ?? ''}`;
            state.orderLegActiveTab = { tabValue: firstTabValue, isMaster: firstTabValue.includes('isMaster-') };
            state.loading = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getOrderDetailsAsync.pending, (state, action: any) => {
                // Silent = socket-driven background refresh (see useOrderLiveSync). Keep the
                // rendered order on screen: flipping loading/clearing the data here would
                // flash the whole detail page into skeletons every time WAL touches the order.
                if (action.meta?.arg?.silent) {
                    return;
                }

                state.loading = true;
                state.orderDetailsData = null; // clear stale data so previous order never bleeds in
                state.error = null;
            })
            .addCase(getOrderDetailsAsync.fulfilled, (state, action: any) => {
                const { payload } = action;
                const isSilent = !!action.meta?.arg?.silent;
                const masterOrder = payload?.orderDetailsData?.order;
                let orderLegsRaw: any[] = [];

                if (masterOrder?.orderLegs) {
                    orderLegsRaw = Array.isArray(masterOrder.orderLegs) ? masterOrder.orderLegs : JSON.parse(masterOrder.orderLegs);
                }

                const hasMultipleLegs = orderLegsRaw.length > 1;

                state.loading = false;
                state.error = null;
                state.orderDetailsData = masterOrder || null;
                state.currentLegData = orderLegsRaw[0] || null;
                // orderLocationUpdateList is sorted DESC by createdAt — first entry is the most recent
                state.currentLocation = masterOrder?.orderLocationUpdateList?.[0] || masterOrder?.currentLocation || null;
                state.isOrderHasLeg = hasMultipleLegs;

                // Build the leg selector items — see buildOrderLegTabs for the identity contract
                state.orderLegs = buildOrderLegTabs(masterOrder, orderLegsRaw);

                // Default selection: the master tab — for single-leg orders it is now the
                // ONLY tab (see buildOrderLegTabs). Reset alongside currentLegData so
                // highlight and content can never desync on refetch.
                //
                // Exception: a silent (socket-driven) refresh must NOT move the customer —
                // they are looking at a leg tab and WAL approving a request elsewhere should
                // not yank them back to Master. Keep the selection when that tab still exists
                // in the fresh payload, and re-point currentLegData at the SAME leg so the two
                // stay in sync (the rule the reset above exists to protect).
                const previousTabValue = state.orderLegActiveTab?.tabValue;
                const previousLeg = orderLegsRaw.find((leg: any) => `${leg?.id}` === previousTabValue);
                const isPreviousTabStillValid = previousTabValue === `isMaster-${masterOrder?.id}` || !!previousLeg;

                if (isSilent && isPreviousTabStillValid) {
                    state.currentLegData = previousLeg || orderLegsRaw[0] || null;
                } else {
                    state.orderLegActiveTab = {
                        tabValue: `isMaster-${masterOrder?.id}`,
                        isMaster: true,
                    };
                }
            })
            .addCase(getOrderDetailsAsync.rejected, (state, action: any) => {
                state.loading = false;

                // A failed silent refresh must not publish an error: OrderView navigates away
                // on a 404 / invalid-params error, so a transient background failure would
                // bounce the customer off a page they are actively reading. Keep the last
                // good data and wait for the next event.
                if (action.meta?.arg?.silent) {
                    return;
                }

                state.error = action.payload;
            });
    },
});

// ─── Exports ──────────────────────────────────────────────────────────────────

export default OrdersSlice.reducer;

export const {
    resetOrderDetailsState,
    setOrderLoading,
    setOrderDetailsData,
    setCurrentLegData,
    setOrderLegActiveTab,
    setParams,
    updateOrderField,
    updateCurrentLegField,
    removeLegDocument,
    setLegDocumentsState,
    setOrderData,
} = OrdersSlice.actions;

// ─── Selectors ────────────────────────────────────────────────────────────────

// Reference-compatible selectors (match wal-frontend-v2 naming)
export const orderDetailsData = (state: RootState) => state.orders.orderDetailsData;
export const orderDetailsLoading = (state: RootState): boolean => state.orders.loading || state.orders.isOrderServiceLoading;
export const orderDetailsError = (state: RootState) => state.orders.error;
export const params = (state: RootState) => state.orders.params;

// Legacy select* selectors (backward-compat — point to renamed fields)
export const selectOrder = (state: RootState) => state.orders.orderDetailsData;
export const selectOrderLegs = (state: RootState) => state.orders.orderLegs;

// True only when there is a leg tab bar to interact with — master + at least one leg. A
// single-presented-leg order gets just the master tab (see buildOrderLegTabs), the bar is
// hidden for it, and its master tab IS that one leg — so leg-scoped sections read the leg's
// own data rather than the order-level rollup. Shared so the bar and those sections can
// never disagree about what counts as multi-leg.
export const selectHasLegTabs = (state: RootState) => (state.orders.orderLegs?.length ?? 0) > 1;
export const selectCurrentLegData = (state: RootState) => state.orders.currentLegData;
export const selectOrderLegActiveTab = (state: RootState) => state.orders.orderLegActiveTab;
export const selectCurrentLocation = (state: RootState) => state.orders.currentLocation;
export const selectOrderLoading = (state: RootState) => state.orders.loading;
export const selectLegDocumentsState = (state: RootState) => state.orders.legDocuments;
